import { SearchEntity, SearchResultGroup } from "../../services/search/search.types";

// The backend response isn't typed at runtime, so CommandPalette filters out
// any group whose entity isn't a key of this record (see index.tsx) instead
// of crashing on the lookup — hence the Partial.
export const ENTITY_GROUP_LABEL: Partial<Record<SearchEntity, string>> = {
  program: "Programas",
  course: "Cursos",
  lesson: "Lecciones",
  resource: "Recursos",
  game_quiz: "Quizzes Emmi",
  school: "Escuelas",
  user: "Usuarios",
};

// A distinct antd preset color per entity so the type badge is scannable at a
// glance. Presets are theme-adaptive (no hardcoded hex). Lives here (and not
// in PreviewPanel) so other entity badges — e.g. the attention queue — can
// reuse it without dragging the panel's service imports into their graph.
export const ENTITY_TAG_COLOR: Record<SearchEntity, string> = {
  program: "purple",
  course: "blue",
  lesson: "cyan",
  resource: "geekblue",
  game_quiz: "magenta",
  school: "gold",
  user: "green",
};

// Checked against src/routes/routes.tsx: every entity has a standalone
// detail route (program, course, lesson and resource open their dedicated
// form; game_quiz/school/user also have a `/:id` detail route —
// "/game-quizzes/:id", "/schools/:id" and "/users/:id" all exist), so no
// entity needs to fall back to its list route.
const DETAIL_PATH_BY_ENTITY: Partial<Record<SearchEntity, string>> = {
  program: "/programs",
  course: "/courses",
  lesson: "/lessons",
  resource: "/resources",
  game_quiz: "/game-quizzes",
  school: "/schools",
  user: "/users",
};

export const getNavigationPath = (entity: SearchEntity, id: number): string =>
  `${DETAIL_PATH_BY_ENTITY[entity]}/${id}`;

// Route for a hierarchy ancestor whose `entity` is a raw backend string.
// Returns null when the entity has no known detail route so callers render
// plain text instead of inventing (e.g. "undefined/5") paths.
export const getHierarchyPath = (
  entity: string,
  id: number
): string | null => {
  const path = DETAIL_PATH_BY_ENTITY[entity as SearchEntity];
  return path ? `${path}/${id}` : null;
};

export interface FlatSearchResult {
  key: string;
  entity: SearchEntity;
  id: number;
  title: string;
  subtitle: string | null;
}

/** Flattens grouped search results into a single ordered list, used for
 * keyboard navigation (arrow up/down + Enter) across group boundaries. */
export const flattenResults = (
  results: SearchResultGroup[]
): FlatSearchResult[] =>
  results.flatMap((group) =>
    group.items.map((item) => ({
      key: `${group.entity}-${item.id}`,
      entity: group.entity,
      id: item.id,
      title: item.title,
      subtitle: item.subtitle,
    }))
  );
